import {useMemo} from 'react';
import {useSyncExternalStore} from 'use-sync-external-store/shim';

import type {ActionResult} from '../actionResult';
import type {SelectedPanel, SelectedRun} from '../composer';
import {
  publishExternalWBAgentTurnCreated,
  subscribeWBAgentCrossTabEvents,
} from '../crossTabSync';
import type {ContentPart} from '../store/thread';
import {
  markThreadReadAndClearUnseen,
  observeThreadAsRead,
} from '../threadReadStatus';
import {
  type StartChatOptions,
  type WBAgentController,
  type WBAgentControllerStore,
  wbAgentControllerStore,
} from './store';

/**
 * WBAgentControllerApi is the app-facing controller returned by
 * `useWBAgentController`. It adds `isAvailable` and widens `startChat` with
 * an `unavailable` outcome for when no chat is mounted to receive the message.
 */
export type WBAgentControllerApi = Omit<
  WBAgentController,
  'addPanelToChat' | 'addRunToChat' | 'startChat'
> & {
  isAvailable: boolean;
  addPanelToChat(
    panel: SelectedPanel
  ): ActionResult<'readonly' | 'unavailable'>;
  addRunToChat(run: SelectedRun): ActionResult<'readonly' | 'unavailable'>;
  startChat(
    prompt: string | ContentPart[],
    opts: StartChatOptions
  ): ActionResult<'readonly' | 'empty' | 'occupied' | 'unavailable'>;
  /**
   * Registers a persisted root turn created outside Chat so Chat can hydrate
   * and follow it without opening the Chat UI.
   */
  registerExternalTurn(turn: {threadId: string; turnId: string}): void;
  /** Runs `listener` when another surface announces a new persisted turn. */
  subscribeExternalTurnCreated(listener: () => void): () => void;
  /**
   * Suppresses Chat's completion notification while another visible surface
   * is presenting this thread. Returns a disposer for that observation.
   */
  observeThreadAsRead(threadId: string, isObserved?: () => boolean): () => void;
  /** Marks an externally-presented thread read without opening Chat. */
  markThreadRead(threadId: string): void;
};

/**
 * useWBAgentController exposes the agent chat, whether it's available and
 * various interactions, to any component in the app. `store` defaults to the
 * shared app-wide instance; tests pass their own for isolation.
 */
export function useWBAgentController(
  store: WBAgentControllerStore = wbAgentControllerStore
): WBAgentControllerApi {
  // NOTE: isAvailable trails the live runtime by one commit. Fine for buttons,
  // but never gate a useAui-rendering component on it; those belong inside
  // AgentChatRuntimeBoundary.
  const {isAvailable} = useSyncExternalStore(
    store.subscribe,
    store.getAvailability
  );
  const actions = useMemo(
    () => ({
      addPanelToChat: (
        panel: SelectedPanel
      ): ActionResult<'readonly' | 'unavailable'> =>
        store.getHandle()?.addPanelToChat(panel) ?? {
          ok: false,
          reason: 'unavailable',
        },
      addRunToChat: (
        run: SelectedRun
      ): ActionResult<'readonly' | 'unavailable'> =>
        store.getHandle()?.addRunToChat(run) ?? {
          ok: false,
          reason: 'unavailable',
        },
      startChat: (
        prompt: string | ContentPart[],
        opts: StartChatOptions
      ): ActionResult<'readonly' | 'empty' | 'occupied' | 'unavailable'> =>
        store.getHandle()?.startChat(prompt, opts) ?? {
          ok: false,
          reason: 'unavailable',
        },
      openChat: () => store.getHandle()?.openChat(),
      openChatToThread(threadId: string) {
        store.getHandle()?.openChatToThread(threadId);
      },
      registerExternalTurn({
        threadId,
        turnId,
      }: {
        threadId: string;
        turnId: string;
      }) {
        try {
          publishExternalWBAgentTurnCreated({
            type: 'turn-created',
            threadId,
            turnId,
          });
        } catch {
          // The persisted turn remains authoritative when notification fails.
        }
      },
      subscribeExternalTurnCreated(listener: () => void) {
        return subscribeWBAgentCrossTabEvents(event => {
          if (event.type === 'turn-created') {
            listener();
          }
        });
      },
      observeThreadAsRead,
      markThreadRead: markThreadReadAndClearUnseen,
    }),
    [store]
  );

  return {isAvailable, ...actions};
}
